import { useEffect, useRef, useState } from 'react';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import config from '../config.js';

/**
 * A live price for a new request (ENG-709) from
 * `GET /public/bands/{id}/songs/{songId}/request-quote` (ENG-710), on the
 * boost desk's rules.
 *
 * `request` is `{ target }` (a rail spot), `{ amount }` (dollars) or `{}`
 * (the $1 minimum). `queueKey` changes when the queue does, so the price is
 * always for the queue as it is. Answers that arrive after a newer request
 * are dropped.
 */
export const useRequestQuote = (bandId, songId, request, queueKey) => {
  const fetchWrapperRef = useRef(useFetchWrapper());
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const seqRef = useRef(0);
  const target = request?.target;
  const amount = request?.amount;

  useEffect(() => {
    if (!bandId || !songId) {
      setQuote(null);
      return undefined;
    }
    const seq = ++seqRef.current;
    const params = new URLSearchParams();
    if (target != null) params.set('target', String(target));
    else if (amount != null) params.set('amount', String(amount));
    const query = params.toString();
    setLoading(true);
    fetchWrapperRef.current
      .get(`${config.apiBaseUrl}/public/bands/${bandId}/songs/${songId}/request-quote${query ? `?${query}` : ''}`)
      .then((answer) => {
        if (seq !== seqRef.current) return;
        setQuote(answer);
        setError(null);
      })
      .catch((err) => {
        if (seq !== seqRef.current) return;
        setError(err?.code || 'request_quote_failed');
      })
      .finally(() => {
        if (seq === seqRef.current) setLoading(false);
      });
    return () => { seqRef.current += 1; };
  }, [bandId, songId, target, amount, queueKey]);

  return { quote, error, loading };
};
